import { Suspense, lazy } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { Permission } from '@gymos/shared';
import { RequireAuth, RequireMember, RequirePermission, RedirectIfAuthenticated } from '@/components/guards';
import { RouteErrorBoundary } from '@/components/AppErrorBoundary';
import { FullPageLoader, RouteFallback } from '@/components/FullPageLoader';

// Route chunks are lazy so the first paint ships the shell, not the whole console.
const StaffLayout = lazy(() => import('@/layouts/StaffLayout'));
const MemberLayout = lazy(() => import('@/layouts/MemberLayout'));
const StaffLogin = lazy(() => import('@/pages/auth/StaffLogin'));
const MemberLogin = lazy(() => import('@/pages/auth/MemberLogin'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// --- Staff console ---
const Dashboard = lazy(() => import('@/pages/staff/Dashboard'));
const LeadsPage = lazy(() => import('@/pages/staff/leads/LeadsPage'));
const FollowupsPage = lazy(() => import('@/pages/staff/followups/FollowupsPage'));
const ActivityTypesPage = lazy(() => import('@/pages/staff/settings/ActivityTypesPage'));
const LeadDetailPage = lazy(() => import('@/pages/staff/leads/LeadDetailPage'));
const MembersPage = lazy(() => import('@/pages/staff/members/MembersPage'));
const MemberDetailPage = lazy(() => import('@/pages/staff/members/MemberDetailPage'));
const AttendancePage = lazy(() => import('@/pages/staff/attendance/AttendancePage'));
const KioskPage = lazy(() => import('@/pages/staff/attendance/KioskPage'));
const StaffSlotsPage = lazy(() => import('@/pages/staff/attendance/SlotsPage'));
const InvoicesPage = lazy(() => import('@/pages/staff/billing/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/staff/billing/InvoiceDetailPage'));
const PaymentsPage = lazy(() => import('@/pages/staff/billing/PaymentsPage'));
const PlansPage = lazy(() => import('@/pages/staff/billing/PlansPage'));
const RenewalsPage = lazy(() => import('@/pages/staff/billing/RenewalsPage'));
const PtPage = lazy(() => import('@/pages/staff/pt/PtPage'));
const SessionsPage = lazy(() => import('@/pages/staff/pt/SessionsPage'));
const WorkoutsPage = lazy(() => import('@/pages/staff/fitness/WorkoutsPage'));
const DietsPage = lazy(() => import('@/pages/staff/fitness/DietsPage'));
const ExercisesPage = lazy(() => import('@/pages/staff/fitness/ExercisesPage'));
const StaffFeedbackPage = lazy(() => import('@/pages/staff/feedback/FeedbackPage'));
const InventoryPage = lazy(() => import('@/pages/staff/inventory/InventoryPage'));
const EquipmentPage = lazy(() => import('@/pages/staff/inventory/EquipmentPage'));
const WhatsAppPage = lazy(() => import('@/pages/staff/messaging/WhatsAppPage'));
const TemplatesPage = lazy(() => import('@/pages/staff/messaging/TemplatesPage'));
const CampaignsPage = lazy(() => import('@/pages/staff/messaging/CampaignsPage'));
const AutomationsPage = lazy(() => import('@/pages/staff/messaging/AutomationsPage'));
const ReportsPage = lazy(() => import('@/pages/staff/reports/ReportsPage'));
const TeamPage = lazy(() => import('@/pages/staff/team/TeamPage'));
const StaffAttendancePage = lazy(() => import('@/pages/staff/team/AttendancePage'));
const LeavePage = lazy(() => import('@/pages/staff/team/LeavePage'));
const SalaryPage = lazy(() => import('@/pages/staff/team/SalaryPage'));
const IncentivesPage = lazy(() => import('@/pages/staff/team/IncentivesPage'));
const PayrollPage = lazy(() => import('@/pages/staff/team/PayrollPage'));
const CompaniesPage = lazy(() => import('@/pages/staff/corporate/CompaniesPage'));
const CompanyPage = lazy(() => import('@/pages/staff/corporate/CompanyPage'));
const MyWorkPage = lazy(() => import('@/pages/staff/team/MyWorkPage'));
const SettingsPage = lazy(() => import('@/pages/staff/settings/SettingsPage'));
const UsersPage = lazy(() => import('@/pages/staff/settings/UsersPage'));
const BranchesPage = lazy(() => import('@/pages/staff/settings/BranchesPage'));

// --- Member PWA ---
const MemberHome = lazy(() => import('@/pages/member/MemberHome'));
const CheckInPage = lazy(() => import('@/pages/member/CheckInPage'));
const MemberSlotsPage = lazy(() => import('@/pages/member/SlotsPage'));
const PlanPage = lazy(() => import('@/pages/member/PlanPage'));
const MemberInvoicesPage = lazy(() => import('@/pages/member/InvoicesPage'));
const ProgressPage = lazy(() => import('@/pages/member/ProgressPage'));
const WorkoutPage = lazy(() => import('@/pages/member/WorkoutPage'));
const DietPage = lazy(() => import('@/pages/member/DietPage'));
const MemberFeedbackPage = lazy(() => import('@/pages/member/FeedbackPage'));
const ProfilePage = lazy(() => import('@/pages/member/ProfilePage'));

/** Wraps a lazily-loaded element that is *not* already inside a layout Suspense. */
function lazyEl(node: React.ReactNode, fallback: React.ReactNode = <FullPageLoader />) {
  return <Suspense fallback={fallback}>{node}</Suspense>;
}

/** A route body inside a layout — the layout already owns the page chrome. */
function page(node: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

/**
 * Permission-gated route element.
 *
 * The permission strings here are the same ones the API enforces (both read from
 * `packages/shared/src/roles.ts`), so a route the UI shows is always a route the
 * server will actually serve. Getting these out of sync is how you end up with a
 * navigation item that 403s.
 */
function gated(permission: Permission, node: React.ReactNode) {
  return <RequirePermission permission={permission}>{page(node)}</RequirePermission>;
}

const staffRoutes: RouteObject[] = [
  { index: true, element: page(<Dashboard />) },

  // --- CRM ---
  { path: 'leads', element: gated('lead:read', <LeadsPage />) },
  { path: 'leads/:id', element: gated('lead:read', <LeadDetailPage />) },

  // --- Follow-ups (leads and members in one queue) ---
  { path: 'followups', element: gated('followup:read', <FollowupsPage />) },
  { path: 'settings/activity-types', element: gated('settings:write', <ActivityTypesPage />) },

  // --- Members ---
  { path: 'members', element: gated('member:read', <MembersPage />) },
  { path: 'members/:id', element: gated('member:read', <MemberDetailPage />) },

  // --- Attendance ---
  { path: 'attendance', element: gated('attendance:read', <AttendancePage />) },
  { path: 'attendance/kiosk', element: gated('attendance:write', <KioskPage />) },
  { path: 'slots', element: gated('slot:read', <StaffSlotsPage />) },

  // --- Billing ---
  { path: 'billing/invoices', element: gated('invoice:read', <InvoicesPage />) },
  { path: 'billing/invoices/:id', element: gated('invoice:read', <InvoiceDetailPage />) },
  { path: 'billing/payments', element: gated('payment:read', <PaymentsPage />) },
  { path: 'billing/plans', element: gated('plan:read', <PlansPage />) },
  { path: 'subscriptions/renewals', element: gated('subscription:read', <RenewalsPage />) },

  // --- Training ---
  { path: 'pt', element: gated('pt:read', <PtPage />) },
  { path: 'pt/sessions', element: gated('session:read', <SessionsPage />) },
  { path: 'fitness/workouts', element: gated('workout:read', <WorkoutsPage />) },
  { path: 'fitness/diets', element: gated('diet:read', <DietsPage />) },
  { path: 'fitness/exercises', element: gated('workout:read', <ExercisesPage />) },

  // --- Ops ---
  { path: 'feedback', element: gated('feedback:read', <StaffFeedbackPage />) },
  { path: 'inventory', element: gated('inventory:read', <InventoryPage />) },
  { path: 'inventory/equipment', element: gated('inventory:read', <EquipmentPage />) },

  // --- Messaging ---
  { path: 'messaging/whatsapp', element: gated('message:read', <WhatsAppPage />) },
  { path: 'messaging/templates', element: gated('message:template', <TemplatesPage />) },
  { path: 'messaging/campaigns', element: gated('message:campaign', <CampaignsPage />) },
  { path: 'messaging/automations', element: gated('message:template', <AutomationsPage />) },

  // --- Team / people ops ---
  { path: 'team', element: gated('roster:read', <TeamPage />) },
  { path: 'team/attendance', element: gated('roster:read', <StaffAttendancePage />) },
  { path: 'team/leave', element: gated('leave:read', <LeavePage />) },
  { path: 'team/salary', element: gated('salary:read', <SalaryPage />) },
  { path: 'team/incentives', element: gated('incentive:read', <IncentivesPage />) },
  { path: 'team/payroll', element: gated('payroll:read', <PayrollPage />) },
  { path: 'companies', element: gated('company:read', <CompaniesPage />) },
  { path: 'companies/:id', element: gated('company:read', <CompanyPage />) },
  // Ungated on purpose: every staff member sees their own attendance, leave,
  // earnings and payslips. The API scopes it to the caller.
  { path: 'team/me', element: page(<MyWorkPage />) },

  // --- Analytics + settings ---
  { path: 'reports', element: gated('report:read', <ReportsPage />) },
  { path: 'settings', element: gated('settings:read', <SettingsPage />) },
  { path: 'settings/users', element: gated('user:read', <UsersPage />) },
  { path: 'settings/branches', element: gated('branch:read', <BranchesPage />) },
];

/**
 * Member PWA routes.
 *
 * No permission gates: every one of these calls `/api/me/*`, which authorises by
 * "is this row mine?" rather than by role. There is no member permission to check.
 */
const memberRoutes: RouteObject[] = [
  { index: true, element: page(<MemberHome />) },
  { path: 'checkin', element: page(<CheckInPage />) },
  { path: 'slots', element: page(<MemberSlotsPage />) },
  { path: 'plan', element: page(<PlanPage />) },
  { path: 'invoices', element: page(<MemberInvoicesPage />) },
  { path: 'progress', element: page(<ProgressPage />) },
  { path: 'workout', element: page(<WorkoutPage />) },
  { path: 'diet', element: page(<DietPage />) },
  { path: 'feedback', element: page(<MemberFeedbackPage />) },
  { path: 'profile', element: page(<ProfilePage />) },
];

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <RedirectIfAuthenticated>{lazyEl(<StaffLogin />)}</RedirectIfAuthenticated>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/m/login',
    element: <RedirectIfAuthenticated>{lazyEl(<MemberLogin />)}</RedirectIfAuthenticated>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: <RequireAuth>{lazyEl(<StaffLayout />)}</RequireAuth>,
    errorElement: <RouteErrorBoundary />,
    children: staffRoutes,
  },
  {
    path: '/m',
    element: <RequireMember>{lazyEl(<MemberLayout />)}</RequireMember>,
    errorElement: <RouteErrorBoundary />,
    children: memberRoutes,
  },
  {
    path: '*',
    element: lazyEl(<NotFound />),
  },
]);
